import { glMatrix, vec4 } from 'gl-matrix'

import type {
  HorizonAlignment,
  IWebGlRender,
  MsdfFont,
  Nullable,
  Point2D,
  Property,
  ScaleType,
  Size,
  VertexAttribute,
  VerticalAlignment,
} from '../../../../types'
import {
  bindAttributes,
  bindIndexBuffer,
  createAttribute,
  createIndexBufferUint16,
  deleteAttributes,
  getOrCreateProgram,
  getOrCreateTexture,
  getWebGlContext,
  unbindAttributes,
  updateAttributeBuffer,
} from '../../../../utils'
import {
  createFontTexture,
  createTransformMatrix,
  getChar,
  getKerning,
  getSymbolPositions,
  getSymbolTextureCoordinates,
} from './helpers'
import { fragmentShaderSource, vertexShaderSource } from './shaders'

const VERTICIES_PER_SYMBOL = 6
type Attributes =
  | 'aPosition'
  | 'aTextureCoord'
  | 'aColor'
  | 'aThickness'
  | 'aOutlineColor'
  | 'aOutlineThickness'
  | 'aBackgroundColor'

export class LabelRender implements IWebGlRender, Disposable {
  private canvas: HTMLCanvasElement
  private program: WebGLProgram
  private renderingContext: WebGL2RenderingContext

  private atlasBuffer: Nullable<WebGLTexture>

  private attributes: Record<Attributes, VertexAttribute>

  private indexBuffer: Nullable<WebGLBuffer>

  private projectionUniform: Nullable<WebGLUniformLocation>
  private opacityUniform: Nullable<WebGLUniformLocation>
  private atlasUniform: Nullable<WebGLUniformLocation>
  private sdfParamsUniform: Nullable<WebGLUniformLocation>
  private isOutlinePassUniform: Nullable<WebGLUniformLocation>
  private isFillBackgroundUniform: Nullable<WebGLUniformLocation>

  private coordinates: Point2D
  private rotate: number
  private font: MsdfFont
  private text: string
  private opacity: number
  private thickness: number
  private color: vec4
  private isOutlinePass: boolean
  private outlineThickness: number
  private outlineColor: vec4
  private fontSize: number
  private horizonAlignment: HorizonAlignment
  private verticalAlignment: VerticalAlignment
  private isFillBackground: boolean
  private backgroundColor: vec4

  private zoom: ScaleType
  private isScaling: boolean

  private size: Size

  private isDisposed: boolean

  constructor(
    canvas: HTMLCanvasElement,
    programs: Property<Record<string, WebGLProgram>>,
    textures: Property<Record<string, WebGLTexture>>,
    zoom: ScaleType,
    coordinates: Point2D,
    rotate: number,
    font: MsdfFont,
    fontSize: number,
    text: string,
    opactiy: number,
    thickness: number,
    color: vec4,
    isOutlinePass: boolean = false,
    outlineThickness?: number,
    outlineColor?: vec4,
    horizonAlignment: HorizonAlignment = 'center',
    verticalAlignment: VerticalAlignment = 'center',
    isFillBackground: boolean = false,
    backgroundColor?: vec4,
    isScaling: boolean = false,
  ) {
    this.canvas = canvas
    this.zoom = zoom
    this.coordinates = coordinates
    this.rotate = rotate
    this.opacity = opactiy
    this.text = text
    this.thickness = thickness
    this.color = vec4.clone(color)
    this.isOutlinePass = isOutlinePass
    this.outlineThickness = outlineThickness ?? 0
    this.outlineColor = outlineColor ? vec4.clone(outlineColor) : vec4.fromValues(0, 0, 0, 0)
    this.fontSize = fontSize
    this.horizonAlignment = horizonAlignment
    this.verticalAlignment = verticalAlignment
    this.isFillBackground = isFillBackground
    this.backgroundColor = backgroundColor
      ? vec4.clone(backgroundColor)
      : vec4.fromValues(0, 0, 0, 0)
    this.isScaling = isScaling

    this.size = { width: 0, height: 0 }

    const gl = getWebGlContext(canvas)
    this.renderingContext = gl

    const program = getOrCreateProgram(programs, gl, vertexShaderSource, fragmentShaderSource)
    this.renderingContext = gl
    this.program = program

    this.attributes = {
      aPosition: createAttribute(gl, program, 'aPosition', 2, gl.FLOAT),
      aTextureCoord: createAttribute(gl, program, 'aTextureCoord', 2, gl.FLOAT),
      aThickness: createAttribute(gl, program, 'aThickness', 1, gl.FLOAT),
      aColor: createAttribute(gl, program, 'aColor', 4, gl.FLOAT, false),
      aOutlineThickness: createAttribute(gl, program, 'aOutlineThickness', 1, gl.FLOAT),
      aOutlineColor: createAttribute(gl, program, 'aOutlineColor', 4, gl.FLOAT, false),
      aBackgroundColor: createAttribute(gl, program, 'aBackgroundColor', 4, gl.FLOAT, false),
    }

    this.indexBuffer = null

    this.projectionUniform = gl.getUniformLocation(program, 'uProjection')
    this.opacityUniform = gl.getUniformLocation(program, 'uOpacity')
    this.atlasUniform = gl.getUniformLocation(program, 'uImage')
    this.sdfParamsUniform = gl.getUniformLocation(program, 'uSdfParams')
    this.isOutlinePassUniform = gl.getUniformLocation(program, 'uIsOutlinePass')
    this.isFillBackgroundUniform = gl.getUniformLocation(program, 'uIsFillBackGround')

    this.font = font

    this.atlasBuffer = getOrCreateTexture(textures, font.atlas.src, () =>
      createFontTexture(gl, font),
    )
    this.setText(text)

    this.isDisposed = false
  }

  private setUniforms() {
    const renderingContext = this.renderingContext

    renderingContext.uniform1i(this.atlasUniform, 0)

    renderingContext.uniformMatrix3fv(
      this.projectionUniform,
      false,
      createTransformMatrix(
        this.canvas,
        this.zoom,
        glMatrix.toRadian(this.rotate),
        this.coordinates,
        this.size,
        this.horizonAlignment,
        this.verticalAlignment,
        this.isScaling,
      ),
    )
    renderingContext.uniform1f(this.opacityUniform, this.opacity)
    renderingContext.uniform4f(
      this.sdfParamsUniform,
      this.font.config.common.scaleW,
      this.font.config.common.scaleH,
      this.font.config.info.size,
      this.font.config.distanceField.distanceRange,
    )
    renderingContext.uniform1i(this.isOutlinePassUniform, this.isOutlinePass ? 1 : 0)
    renderingContext.uniform1i(this.isFillBackgroundUniform, this.isFillBackground ? 1 : 0)
  }

  public render() {
    if (this.isDisposed) return

    const renderingContext = this.renderingContext

    renderingContext.enable(renderingContext.BLEND)
    renderingContext.blendFunc(renderingContext.ONE, renderingContext.ONE_MINUS_SRC_ALPHA)

    bindAttributes(renderingContext, this.attributes)

    bindIndexBuffer(renderingContext, this.indexBuffer)

    renderingContext.activeTexture(renderingContext.TEXTURE0)
    renderingContext.bindTexture(renderingContext.TEXTURE_2D, this.atlasBuffer)

    renderingContext.useProgram(this.program)

    this.setUniforms()

    renderingContext.drawElements(
      renderingContext.TRIANGLES,
      this.text.length * VERTICIES_PER_SYMBOL,
      renderingContext.UNSIGNED_SHORT,
      0,
    )

    unbindAttributes(renderingContext, this.attributes)

    renderingContext.bindBuffer(renderingContext.ELEMENT_ARRAY_BUFFER, null)
    renderingContext.bindTexture(renderingContext.TEXTURE_2D, null)
  }

  public setZoomState(zoom: ScaleType) {
    this.zoom = zoom
  }

  public setPoint(point: Point2D) {
    this.coordinates = { ...point }
  }

  public getPoint(): Readonly<Point2D> {
    return this.coordinates
  }

  public setText(text: string) {
    this.text = text
    this.updateAttributeBuffers()
  }

  public setFontSize(size: number) {
    this.fontSize = size
    this.updateAttributeBuffers()
  }

  public setBackgroundColor(backgroundColor: vec4) {
    this.backgroundColor = backgroundColor
    this.updateAttributeBuffers()
  }

  private updateAttributeBuffers() {
    const gliphSize = this.fontSize / this.font.config.info.size

    const positions: Point2D[] = []
    const textureCoords: Point2D[] = []
    const indices: number[] = []
    const color: vec4[] = []
    const thickness: number[] = []
    const outlineThickness: number[] = []
    const outlineColor: vec4[] = []
    const backgroundColor: vec4[] = []

    let offsetX = 0
    for (let index = 0; index < this.text.length; index++) {
      const char = getChar(this.text[index], this.font.config)
      if (!char) continue

      const positionsCount = positions.length

      textureCoords.push(...getSymbolTextureCoordinates(char, this.font.config))

      positions.push(...getSymbolPositions(char, this.font.config, this.fontSize, offsetX))

      thickness.push(this.thickness)
      thickness.push(this.thickness)
      thickness.push(this.thickness)
      thickness.push(this.thickness)

      color.push(this.color)
      color.push(this.color)
      color.push(this.color)
      color.push(this.color)

      outlineThickness.push(this.outlineThickness)
      outlineThickness.push(this.outlineThickness)
      outlineThickness.push(this.outlineThickness)
      outlineThickness.push(this.outlineThickness)

      outlineColor.push(this.outlineColor)
      outlineColor.push(this.outlineColor)
      outlineColor.push(this.outlineColor)
      outlineColor.push(this.outlineColor)

      backgroundColor.push(this.backgroundColor)
      backgroundColor.push(this.backgroundColor)
      backgroundColor.push(this.backgroundColor)
      backgroundColor.push(this.backgroundColor)

      indices.push(positionsCount)
      indices.push(positionsCount + 1)
      indices.push(positionsCount + 2)
      indices.push(positionsCount + 2)
      indices.push(positionsCount + 3)
      indices.push(positionsCount)

      const nextChar = getChar(this.text[index + 1], this.font.config)
      offsetX += (char.xadvance + getKerning(char, nextChar, this.font.config)) * gliphSize
    }

    const sizeY = Math.max(...positions.map(pos => pos.y))
    this.size = { width: offsetX, height: sizeY }

    updateAttributeBuffer(
      this.renderingContext,
      this.attributes['aPosition'],
      new Float32Array(positions.flatMap(coord => [coord.x, coord.y])),
    )

    this.renderingContext.deleteBuffer(this.indexBuffer)
    this.indexBuffer = createIndexBufferUint16(this.renderingContext, indices)

    updateAttributeBuffer(
      this.renderingContext,
      this.attributes['aTextureCoord'],
      new Float32Array(textureCoords.flatMap(coord => [coord.x, coord.y])),
    )

    updateAttributeBuffer(
      this.renderingContext,
      this.attributes['aThickness'],
      new Float32Array(thickness),
    )

    updateAttributeBuffer(
      this.renderingContext,
      this.attributes['aColor'],
      new Float32Array(color.flatMap(value => [value[0], value[1], value[2], value[3]])),
    )

    updateAttributeBuffer(
      this.renderingContext,
      this.attributes['aOutlineThickness'],
      new Float32Array(outlineThickness),
    )

    updateAttributeBuffer(
      this.renderingContext,
      this.attributes['aOutlineColor'],
      new Float32Array(outlineColor.flatMap(value => [value[0], value[1], value[2], value[3]])),
    )

    updateAttributeBuffer(
      this.renderingContext,
      this.attributes['aBackgroundColor'],
      new Float32Array(backgroundColor.flatMap(value => [value[0], value[1], value[2], value[3]])),
    )
  }

  [Symbol.dispose](): void {
    const renderingContext = this.renderingContext
    renderingContext.bindBuffer(renderingContext.ARRAY_BUFFER, null)
    deleteAttributes(renderingContext, this.attributes)
    renderingContext.deleteBuffer(this.indexBuffer)
    this.isDisposed = true
  }
}
